import React, { useState, useEffect, useRef } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { assessments } from '../../services/api';
import { nomUsuari } from '../../utils/usuari';

function ChatPanel({ assessmentId, isOpen, onToggle }) {
  const { user } = useAuth();
  const { on, sendChatMessage, connected } = useSocket();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  // Load chat history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const { data } = await assessments.getChatHistory(assessmentId);
        setMessages(data);
      } catch (err) {
        console.error('Failed to load chat history:', err);
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, [assessmentId]);

  // Listen for new messages
  useEffect(() => {
    const unsubscribe = on('chat_message', (message) => {
      setMessages(prev => [...prev, {
        id: message.id,
        message: message.message,
        createdAt: message.createdAt,
        user: {
          id: message.userId,
          email: message.userEmail
        }
      }]);
    });
    return unsubscribe;
  }, [on]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !connected) return;

    sendChatMessage(assessmentId, newMessage.trim());
    setNewMessage('');
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-4 right-4 bg-accent text-white p-3 rounded-full shadow-lg hover:bg-accent-hover z-50"
        title="Obrir xat"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-80 h-96 bg-surface rounded-lg shadow-xl border border-border flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-primary-700 bg-accent text-white rounded-t-lg">
        <h3 className="font-medium">Xat de l'equip</h3>
        <div className="flex items-center space-x-2">
          {connected ? (
            <span className="w-2 h-2 bg-green-400 rounded-full" title="Connectat"></span>
          ) : (
            <span className="w-2 h-2 bg-red-400 rounded-full" title="Desconnectat"></span>
          )}
          <button onClick={onToggle} className="hover:bg-accent-hover p-1 rounded">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-surface">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-content-muted text-sm">Encara no hi ha missatges. Comença la conversa!</p>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.user?.id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] ${isOwn ? 'bg-accent text-white' : 'bg-surface-muted text-content'} rounded-lg px-3 py-2`}>
                  {!isOwn && (
                    <p className="text-xs font-medium text-accent mb-1">
                      {nomUsuari(msg.user)}
                    </p>
                  )}
                  <p className="text-sm break-words">{msg.message}</p>
                  <p className={`text-xs mt-1 ${isOwn ? 'text-primary-200' : 'text-content-muted'}`}>
                    {formatTime(msg.createdAt)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 border-t border-border bg-surface">
        <div className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={connected ? "Escriu un missatge..." : "Connectant..."}
            disabled={!connected}
            className="flex-1 input text-sm py-2 bg-surface text-content border-border-strong placeholder-content-disabled"
          />
          <button
            type="submit"
            disabled={!connected || !newMessage.trim()}
            className="btn btn-primary px-3 py-2 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
}

export default ChatPanel;
